/**
 * Narrative & impact-storytelling blocks:
 *  - ImpactLoop       — circular 8-step closed-loop economy diagram
 *  - BrandSynergy     — MealToken24 × VillagePando bridging banner
 *  - PhaseOneCard     — "Where we start" + live/target KPI counters
 *  - WhyOnChain       — transparency callout
 */
import { useEffect, useRef, useState } from "react";
import {
  Sprout,
  ChefHat,
  Coins,
  Wallet,
  HandHeart,
  Building2,
  Sun,
  Leaf,
  ArrowRight,
  MapPin,
  ShieldCheck,
  Link2,
  Landmark,
  Users,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useReveal } from "@/hooks/use-fx";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import solarVillage from "@/assets/story/solar-village.jpg.asset.json";

const SAFFRON = "oklch(0.82 0.18 75)";
const MINT = "oklch(0.86 0.16 165)";

/* ------------------------------------------------------------------ */
/* 1 — Circular Impact Loop                                            */
/* ------------------------------------------------------------------ */

const LOOP_STEPS = [
  { key: "s1", icon: Sprout, tone: MINT },
  { key: "s2", icon: ChefHat, tone: SAFFRON },
  { key: "s3", icon: Coins, tone: SAFFRON },
  { key: "s4", icon: Wallet, tone: SAFFRON },
  { key: "s5", icon: HandHeart, tone: MINT },
  { key: "s6", icon: Building2, tone: MINT },
  { key: "s7", icon: Sun, tone: SAFFRON },
  { key: "s8", icon: Leaf, tone: MINT },
] as const;

export function ImpactLoop() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  const [active, setActive] = useState(0);
  const step = LOOP_STEPS[active]!;
  const StepIcon = step.icon;

  return (
    <section id="impact-loop" className="relative py-20 sm:py-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(55% 40% at 50% 10%, oklch(0.86 0.16 165 / 0.07), transparent 65%)",
        }}
      />
      <div ref={r} className="reveal mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mint">
            // {t("loop.eyebrow")}
          </span>
          <h2 className="mt-3 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-4xl md:text-5xl">
            {t("loop.title")}
          </h2>
          <p className="mt-4 text-sm text-muted-foreground sm:text-base">{t("loop.sub")}</p>
        </div>

        <div className="mt-14 grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* Circle */}
          <div className="relative mx-auto aspect-square w-full max-w-[540px]">
            <svg
              aria-hidden
              viewBox="0 0 100 100"
              className="absolute inset-0 h-full w-full -rotate-90"
            >
              <circle
                cx="50"
                cy="50"
                r="38"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.4"
                strokeDasharray="1.6 2.4"
                className="text-foreground/20"
              />
              <circle
                cx="50"
                cy="50"
                r="38"
                fill="none"
                stroke={step.tone}
                strokeWidth="0.9"
                strokeLinecap="round"
                strokeDasharray="30 208"
                strokeDashoffset={-(active * (238.76 / 8))}
                className="transition-all duration-500 ease-out"
                opacity="0.85"
              />
            </svg>

            {/* Center hub */}
            <div className="absolute left-1/2 top-1/2 w-[46%] -translate-x-1/2 -translate-y-1/2 rounded-3xl glass-strong p-4 text-center sm:p-5">
              <span
                className="mx-auto grid h-10 w-10 place-items-center rounded-xl"
                style={{
                  background: `color-mix(in oklab, ${step.tone} 18%, transparent)`,
                  color: step.tone,
                }}
              >
                <StepIcon className="h-5 w-5" aria-hidden />
              </span>
              <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                {String(active + 1).padStart(2, "0")} / 08
              </div>
              <div className="mt-1 text-xs font-semibold leading-tight text-foreground sm:text-sm">
                {t(`loop.${step.key}.t`)}
              </div>
            </div>

            {LOOP_STEPS.map((s, i) => {
              const angle = (i / LOOP_STEPS.length) * 2 * Math.PI - Math.PI / 2;
              const x = 50 + 38 * Math.cos(angle);
              const y = 50 + 38 * Math.sin(angle);
              const Icon = s.icon;
              const isActive = i === active;
              return (
                <button
                  key={s.key}
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => {
                    setActive(i);
                    track("cta_click", { id: `loop_step_${s.key}`, surface: "impact_loop" });
                  }}
                  aria-current={isActive ? "step" : undefined}
                  aria-label={`${i + 1}. ${t(`loop.${s.key}.t`)}`}
                  className={cn(
                    "absolute grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-2xl glass transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60 sm:h-14 sm:w-14",
                    isActive ? "scale-110" : "opacity-70 hover:opacity-100",
                  )}
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    boxShadow: isActive
                      ? `0 0 0 1px ${s.tone}, 0 14px 40px -18px ${s.tone}`
                      : undefined,
                  }}
                >
                  <Icon
                    className="h-5 w-5"
                    style={{ color: isActive ? s.tone : undefined }}
                    aria-hidden
                  />
                  <span className="absolute -bottom-1.5 -right-1.5 rounded-full bg-obsidian px-1.5 font-mono text-[10px] text-muted-foreground">
                    {i + 1}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div
            aria-live="polite"
            className="rounded-3xl glass-strong p-6 sm:p-7"
            style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${step.tone} 25%, transparent)` }}
          >
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
              style={{
                background: `color-mix(in oklab, ${step.tone} 12%, transparent)`,
                color: step.tone,
              }}
            >
              <StepIcon className="h-3 w-3" aria-hidden />
              {t("loop.step")} {String(active + 1).padStart(2, "0")}
            </span>
            <h3 className="mt-3 font-display text-xl font-semibold tracking-tight sm:text-2xl">
              {t(`loop.${step.key}.t`)}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t(`loop.${step.key}.d`)}
            </p>
            <div className="mt-5 flex items-center gap-2 border-t border-white/10 pt-4 text-xs text-muted-foreground">
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-mint" aria-hidden />
              {t("loop.footnote")}
            </div>
          </div>
        </div>

        {/* Linear fallback / scannable list */}
        <ol className="mt-10 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {LOOP_STEPS.map((s, i) => (
            <li
              key={s.key}
              className={cn(
                "rounded-xl glass px-3 py-2 text-[11px] leading-tight transition-opacity",
                i === active ? "opacity-100" : "opacity-65",
              )}
            >
              <span className="font-mono text-[10px] text-muted-foreground">
                {String(i + 1).padStart(2, "0")}
              </span>{" "}
              <span className="text-foreground/85">{t(`loop.${s.key}.t`)}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 2 — Brand synergy banner                                            */
/* ------------------------------------------------------------------ */

export function BrandSynergy() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  return (
    <section className="relative py-10 sm:py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div
          ref={r}
          className="reveal relative overflow-hidden rounded-3xl glass-strong luminous-border p-6 sm:p-9"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full blur-3xl opacity-40"
            style={{ background: `linear-gradient(135deg, ${SAFFRON}, ${MINT})` }}
          />
          <div className="relative z-10 grid items-center gap-6 md:grid-cols-[auto_1fr]">
            <div className="flex items-center gap-3">
              <span className="rounded-2xl glass px-3.5 py-2 font-display text-sm font-semibold tracking-tight text-saffron">
                MealToken24
              </span>
              <Link2 className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="rounded-2xl glass px-3.5 py-2 font-display text-sm font-semibold tracking-tight text-mint">
                VillagePando
              </span>
            </div>
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("synergy.eyebrow")}
              </span>
              <p className="mt-2 font-display text-lg leading-snug tracking-tight sm:text-xl md:text-2xl">
                {t("synergy.body")}
              </p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {[t("synergy.b1"), t("synergy.b2"), t("synergy.b3")].map((b) => (
                  <li
                    key={b}
                    className="rounded-full glass px-3 py-1.5 text-[11px] text-foreground/85"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 3 — Phase 1 / Where we start + KPI counters                          */
/* ------------------------------------------------------------------ */

const KPIS = [
  { key: "k1", icon: ChefHat, target: 12480, suffix: "", tone: SAFFRON },
  { key: "k2", icon: Landmark, target: 6, suffix: "", tone: MINT },
  { key: "k3", icon: Sun, target: 18400, suffix: " kWh", tone: SAFFRON },
  { key: "k4", icon: Users, target: 3, suffix: "", tone: MINT },
] as const;

function useCountUp(target: number, active: boolean, duration = 1400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
}

function KpiTile({
  icon: Icon,
  label,
  note,
  target,
  suffix,
  tone,
  active,
}: {
  icon: typeof ChefHat;
  label: string;
  note: string;
  target: number;
  suffix: string;
  tone: string;
  active: boolean;
}) {
  const value = useCountUp(target, active);
  return (
    <div className="rounded-2xl glass p-4">
      <span
        className="grid h-9 w-9 place-items-center rounded-xl"
        style={{ background: `color-mix(in oklab, ${tone} 16%, transparent)`, color: tone }}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div
        className="mt-3 font-display text-2xl font-semibold tracking-tight tabular-nums sm:text-3xl"
        style={{ color: tone }}
      >
        {value.toLocaleString("en-US")}
        {suffix}
      </div>
      <div className="mt-1 text-xs font-medium text-foreground/85">{label}</div>
      <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        {note}
      </div>
    </div>
  );
}

export function PhaseOneCard() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  const hostRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section id="phase-one" className="relative py-10 sm:py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div
          ref={r}
          className="reveal grid overflow-hidden rounded-3xl glass-strong luminous-border lg:grid-cols-[1fr_1.25fr]"
        >
          <img
            src={solarVillage.url}
            alt={t("phase1.imgAlt")}
            width={1024}
            height={768}
            loading="lazy"
            decoding="async"
            className="h-56 w-full object-cover lg:h-full"
          />
          <div ref={hostRef} className="p-6 sm:p-8">
            <div className="flex items-center gap-2 text-saffron">
              <MapPin className="h-4 w-4" aria-hidden />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
                {t("phase1.eyebrow")}
              </span>
            </div>
            <h3 className="mt-3 font-display text-2xl font-medium leading-[1.15] tracking-tight sm:text-3xl">
              {t("phase1.title")}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {t("phase1.body")}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {KPIS.map((k) => (
                <KpiTile
                  key={k.key}
                  icon={k.icon}
                  label={t(`phase1.${k.key}.label`)}
                  note={t(`phase1.${k.key}.note`)}
                  target={k.target}
                  suffix={k.suffix}
                  tone={k.tone}
                  active={inView}
                />
              ))}
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t("phase1.kpiNote")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 4 — Why on-chain?                                                    */
/* ------------------------------------------------------------------ */

const WHY_ITEMS = [
  { key: "w1", icon: ShieldCheck, tone: SAFFRON },
  { key: "w2", icon: Sprout, tone: MINT },
  { key: "w3", icon: Users, tone: MINT },
] as const;

export function WhyOnChain() {
  const { t } = useI18n();
  const r = useReveal<HTMLDivElement>();
  return (
    <section className="relative py-10 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div ref={r} className="reveal rounded-3xl glass-strong p-6 sm:p-9">
          <div className="max-w-2xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-saffron">
              // {t("why.eyebrow")}
            </span>
            <h3 className="mt-3 font-display text-2xl font-medium leading-[1.15] tracking-tight sm:text-3xl">
              {t("why.title")}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("why.body")}</p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {WHY_ITEMS.map((w) => (
              <div key={w.key} className="rounded-2xl glass p-5">
                <span
                  className="grid h-9 w-9 place-items-center rounded-xl"
                  style={{
                    background: `color-mix(in oklab, ${w.tone} 16%, transparent)`,
                    color: w.tone,
                  }}
                >
                  <w.icon className="h-4 w-4" aria-hidden />
                </span>
                <div className="mt-3 text-sm font-semibold tracking-tight">
                  {t(`why.${w.key}.t`)}
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                  {t(`why.${w.key}.d`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
